import React, { useMemo } from "react";
import { Col, Row, Button, Card, CardBody } from "reactstrap";
import { FiGrid, FiFolder, FiCornerDownRight } from "react-icons/fi";
import { Select, Spin, TreeSelect } from "antd";
import { FaPlus } from "react-icons/fa";
import FormInput from "../../formInput/FormInput";

// Helper to extract all possible category identifier references from a subcategory object
const getCategoryIdsFromSubCat = (s) => {
    const ids = [];
    if (!s) return ids;
    const addId = (val) => {
        if (val === undefined || val === null || val === "") return;
        if (typeof val === "object") {
            if (val._id) ids.push(String(val._id).trim().toLowerCase());
            if (val.id) ids.push(String(val.id).trim().toLowerCase());
            if (val.categoryName) ids.push(String(val.categoryName).trim().toLowerCase());
            if (val.name) ids.push(String(val.name).trim().toLowerCase());
        } else {
            ids.push(String(val).trim().toLowerCase());
        }
    };
    addId(s.categoryId);
    addId(s.category_id);
    addId(s.category);
    addId(s.parentCategory);
    addId(s.parent_category);
    addId(s.categoryName);
    return ids;
};

const isSubInCat = (sub, cat) => {
    if (!sub || !cat) return false;
    const subCatIds = getCategoryIdsFromSubCat(sub);
    const catKeys = [
        cat._id ? String(cat._id).trim().toLowerCase() : "",
        cat.id ? String(cat.id).trim().toLowerCase() : "",
        cat.categoryName ? String(cat.categoryName).trim().toLowerCase() : "",
        cat.category ? String(cat.category).trim().toLowerCase() : "",
        cat.name ? String(cat.name).trim().toLowerCase() : ""
    ].filter(Boolean);

    return catKeys.some((k) => subCatIds.includes(k));
};

const Step2Categorization = ({
    newItem,
    setNewItem,
    mfg,
    handleMfg,
    handleManufacturerSearch,
    searchedMfg,
    all_manufacturers,
    dataLoading,
    productTypes,
    all_categories,
    all_subCategories,
    selectedCategory,
    setSelectedCategory,
    selectedSubCategory,
    setSelectedSubCategory,
    newItemCondition,
    setMfgModal,
}) => {
    // Construct unified TreeData for Category & SubCategory (shows 100% of categories & subcategories)
    const treeData = useMemo(() => {
        const catList = Array.isArray(all_categories) ? all_categories : [];
        const subList = Array.isArray(all_subCategories) ? all_subCategories : [];

        if (catList.length === 0 && subList.length === 0) return [];

        const data = catList.map((cat) => {
            const catId = cat._id || cat.id;
            const catName = cat.categoryName || cat.category || cat.name || "Unnamed Category";
            const catType = cat.categoryType || cat.category_type || cat.type || "";
            const catKey = `cat_${catId}`;

            // Find subcategories belonging to this category
            const subCats = subList.filter((sub) => isSubInCat(sub, cat));

            return {
                title: (
                    <div
                        className="d-flex align-items-center justify-content-between py-1 pe-2 w-100"
                        style={{ minWidth: 0, gap: "10px" }}
                    >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                            <FiFolder className="text-primary flex-shrink-0" size={14} />
                            {cat.id && (
                                <span
                                    className="badge bg-secondary-subtle text-secondary border font-monospace flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                >
                                    #{cat.id}
                                </span>
                            )}
                            <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                {catName}
                            </span>
                        </div>
                        <span
                            className="badge bg-light text-secondary border flex-shrink-0"
                            style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                        >
                            Category
                        </span>
                    </div>
                ),
                label: `${cat.id ? `[#${cat.id}] ` : ""}${catName}`,
                value: catKey,
                key: catKey,
                searchValue: `${cat.id || ""} ${catName} ${catType} Category`,
                children: subCats.map((sub) => {
                    const subId = sub._id || sub.id;
                    const subName = sub.subCategoryName || sub.subCategory || sub.name || "Unnamed SubCategory";
                    const subKey = `sub_${subId}`;

                    return {
                        title: (
                            <div
                                className="d-flex align-items-center justify-content-between py-1 pe-2 ps-1 w-100"
                                style={{ minWidth: 0, gap: "10px" }}
                            >
                                <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                                    <FiCornerDownRight className="text-muted flex-shrink-0" size={12} />
                                    {sub.id && (
                                        <span
                                            className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace flex-shrink-0"
                                            style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                        >
                                            #{sub.id}
                                        </span>
                                    )}
                                    <span className="text-dark text-truncate" style={{ fontSize: "13px" }}>
                                        {subName}
                                    </span>
                                </div>
                                <span
                                    className="badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                                >
                                    Sub Category
                                </span>
                            </div>
                        ),
                        label: `${sub.id ? `[#${sub.id}] ` : ""}${subName} (${catName})`,
                        value: subKey,
                        key: subKey,
                        searchValue: `${sub.id || ""} ${subName} ${catName} Sub Category`,
                    };
                }),
            };
        });

        // Add any subcategories not mapped to known categories
        const unmappedSubCats = subList.filter((sub) => !catList.some((cat) => isSubInCat(sub, cat)));
        if (unmappedSubCats.length > 0) {
            data.push({
                title: (
                    <div
                        className="d-flex align-items-center justify-content-between py-1 pe-2 w-100"
                        style={{ minWidth: 0, gap: "10px" }}
                    >
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                            <FiFolder className="text-secondary flex-shrink-0" size={14} />
                            <span className="fw-semibold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                Other Subcategories
                            </span>
                        </div>
                        <span
                            className="badge bg-light text-secondary border flex-shrink-0"
                            style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                        >
                            Group
                        </span>
                    </div>
                ),
                label: "Other Subcategories",
                value: "group_unmapped",
                key: "group_unmapped",
                disabled: true,
                selectable: false,
                searchValue: "Other Subcategories",
                children: unmappedSubCats.map((sub) => {
                    const subId = sub._id || sub.id;
                    const subName = sub.subCategoryName || sub.subCategory || sub.name || "Unnamed SubCategory";
                    const subKey = `sub_${subId}`;

                    return {
                        title: (
                            <div
                                className="d-flex align-items-center justify-content-between py-1 pe-2 ps-1 w-100"
                                style={{ minWidth: 0, gap: "10px" }}
                            >
                                <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                                    <FiCornerDownRight className="text-muted flex-shrink-0" size={12} />
                                    {sub.id && (
                                        <span
                                            className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace flex-shrink-0"
                                            style={{ fontSize: "10px", padding: "2px 5px", fontWeight: 600 }}
                                        >
                                            #{sub.id}
                                        </span>
                                    )}
                                    <span className="text-dark text-truncate" style={{ fontSize: "13px" }}>
                                        {subName}
                                    </span>
                                </div>
                                <span
                                    className="badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0"
                                    style={{ fontSize: "10px", padding: "2px 6px", fontWeight: 600, letterSpacing: "0.3px", textTransform: "uppercase" }}
                                >
                                    Sub Category
                                </span>
                            </div>
                        ),
                        label: `${sub.id ? `[#${sub.id}] ` : ""}${subName}`,
                        value: subKey,
                        key: subKey,
                        searchValue: `${sub.id || ""} ${subName} Sub Category`,
                    };
                }),
            });
        }

        return data;
    }, [all_categories, all_subCategories]);

    // Compute currently selected value in TreeSelect
    const selectedTreeValue = useMemo(() => {
        if (selectedSubCategory) {
            const id = selectedSubCategory._id || selectedSubCategory.id;
            if (id) return `sub_${id}`;
        }
        if (selectedCategory) {
            const id = selectedCategory._id || selectedCategory.id;
            if (id) return `cat_${id}`;
        }
        return undefined;
    }, [selectedCategory, selectedSubCategory]);

    // Handle single input selection
    const handleCategoryTreeChange = (val) => {
        if (!val) {
            setSelectedCategory(null);
            setSelectedSubCategory(null);
            return;
        }

        const valStr = String(val);
        if (valStr.startsWith("cat_")) {
            const catId = valStr.replace("cat_", "");
            const matchedCat = all_categories?.find(
                (c) => String(c._id) === catId || String(c.id) === catId
            );
            setSelectedCategory(matchedCat || { _id: catId, id: catId, categoryName: "Category" });
            setSelectedSubCategory(null);

            const catType = matchedCat?.categoryType || matchedCat?.category_type;
            if (catType && (!newItem?.productType || newItem.productType !== catType)) {
                setNewItem((prev) => ({
                    ...prev,
                    productType: catType,
                }));
            }
        } else if (valStr.startsWith("sub_")) {
            const subId = valStr.replace("sub_", "");
            const matchedSub = all_subCategories?.find(
                (s) => String(s._id) === subId || String(s.id) === subId
            );
            if (matchedSub) {
                const { parentCategory, ...subData } = matchedSub;
                setSelectedSubCategory(subData);

                // Find parent category
                let matchedCat = parentCategory;
                if (all_categories?.length > 0) {
                    const found = all_categories.find((c) => isSubInCat(matchedSub, c));
                    if (found) matchedCat = found;
                }
                setSelectedCategory(matchedCat || null);

                const autoType = matchedCat?.categoryType || matchedCat?.category_type || matchedSub?.categoryType || matchedSub?.category_type;
                if (autoType && (!newItem?.productType || newItem.productType !== autoType)) {
                    setNewItem((prev) => ({
                        ...prev,
                        productType: autoType,
                    }));
                }
            }
        }
    };

    return (
        <Card className="border-0 shadow-sm mb-4 bg-light">
            <CardBody className="p-4">
                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                    <div className="bg-primary-soft p-2 rounded-3 text-primary">
                        <FiGrid size={16} />
                    </div>
                    <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider" style={{ fontSize: '13px' }}>Manufacturer & Category</h6>
                </div>
                <Row className="g-3">
                    <Col md={3}>
                        <label className="fw-bold mb-2">MFG Name * </label>
                        <Select
                            size="large"
                            showSearch
                            placeholder={"Search Manufacturer"}
                            filterOption={false}
                            onSelect={(val) => {
                                handleMfg(val);
                            }}
                            value={mfg?.name}
                            onSearch={handleManufacturerSearch}
                            className="mb-2 w-100"
                            getPopupContainer={(trigger) => trigger.parentNode}
                            options={(searchedMfg?.length > 0
                                ? searchedMfg
                                : all_manufacturers
                            )?.map((option) => ({
                                value: option._id,
                                label: `${option.id}-${option.name}`,
                            }))}
                            loading={dataLoading}
                            notFoundContent={
                                dataLoading ? <Spin size="small" /> : "No vendors found"
                            }
                        />
                    </Col>
                    <Col md={3}>
                        <FormInput
                            dropdown
                            type={"select"}
                            label={"Product Type*"}
                            placeholder={"Product Type*"}
                            className={`bg-white border rounded`}
                            size={"large"}
                            value={newItem?.productType ?? ""}
                            options={productTypes}
                            handleChange={(value) => {
                                setNewItem({
                                    ...newItem,
                                    productType: value,
                                });
                            }}
                        />
                    </Col>
                    <Col md={3}>
                        <label className="fw-bold mb-2">Category / Sub Category *</label>
                        <TreeSelect
                            size="large"
                            showSearch
                            allowClear
                            treeDefaultExpandAll
                            treeNodeLabelProp="label"
                            placeholder="Select Category or Sub Category"
                            value={selectedTreeValue}
                            onChange={handleCategoryTreeChange}
                            className="mb-2 w-100"
                            popupMatchSelectWidth={false}
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={(trigger) => trigger.parentNode}
                            dropdownStyle={{
                                minWidth: 400,
                                maxWidth: 540,
                                maxHeight: 400,
                                overflowY: "auto",
                                borderRadius: "8px",
                                padding: "6px 8px",
                                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)"
                            }}
                            treeData={treeData}
                            filterTreeNode={(input, treeNode) => {
                                const searchTarget = (treeNode?.searchValue || treeNode?.label || "").toLowerCase();
                                const tokens = input.toLowerCase().trim().split(/\s+/).filter(Boolean);
                                return tokens.every((token) => searchTarget.includes(token));
                            }}
                        />
                    </Col>
                    <Col md={3}>
                        <FormInput
                            dropdown
                            type={"select"}
                            label={"Condition New*"}
                            placeholder={"Condition New"}
                            className={`bg-white border rounded`}
                            size={"large"}
                            options={newItemCondition}
                            value={newItem?.mfg_condition}
                            handleChange={(value) =>
                                setNewItem({
                                    ...newItem,
                                    mfg_condition: value,
                                })
                            }
                        />
                    </Col>
                    <Col md={12} className="d-flex justify-content-start">
                        <Button
                            className="d-flex align-items-center gap-1"
                            color="primary"
                            onClick={() => setMfgModal(true)}
                        >
                            <FaPlus /> Add Manufacturer
                        </Button>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default Step2Categorization;
